import "core-js/features/array/flat";
import "core-js/proposals/iterator-helpers";
import "core-js/stable";
import path from "path";

import { pathExists, readdir, unlink } from "fs-extra";
import "regenerator-runtime/runtime";
import { config } from "shelljs";

import { runBuildForms } from "./CLICommands/BuildForms/RunBuildForms";
import { runBuildServer, runDevBuildServer } from "./CLICommands/BuildServer/RunBuildServer";
import { runDevBuild } from "./CLICommands/DevBuild/RunDevBuild";
import { runFormStats } from "./CLICommands/FormStats/RunFormStats";
import { runInitForm } from "./CLICommands/InitForm/RunInitForm";
import { runLocalization } from "./CLICommands/Localization/RunLocalization";
import { runUpgradeForm } from "./CLICommands/UpgradeForm/RunUpgradeForm";
import { runConsoleApplication } from "./Commons/ConsoleApplication/ConsoleApplication";
import { ILogger } from "./Commons/Logging/ILogger";
import { setLogger } from "./Commons/Logging/StaticLogger";
import { parseOptions } from "./Options/CandyToolOptions";
import { FarmProxyConfig, readFarmConfig } from "./ReadFarmConfig";
import { getCurrentVersion } from "./SelfUpdate";
import { ConsoleApplicationContext } from "./Commons/ConsoleApplication/ConsoleApplicationContext";
import { runRenameFiles } from "./CLICommands/RenameFiles/RunRenameFiles";
import { clearSourceMapForProduction } from "./CLICommands/BuildForms/ClearSourceMapForProduction";

config.fatal = true;

export interface FarmConfig {
    candyModulesDirectory: string;
    farmDirectory: string;
    engineDirectory?: string | undefined;
    resourcesDirectory?: string | undefined;
    proxy?: Record<string, FarmProxyConfig>;
}

function remoteInspectFlagToDisableDebuggerInChildProcess(): void {
    const debugArgIdx = process.execArgv.indexOf("--inspect-brk");
    if (debugArgIdx !== -1) {
        process.execArgv.splice(debugArgIdx, 1);
    }
    const debugArg2Idx = process.execArgv.indexOf("--inspect");
    if (debugArg2Idx !== -1) {
        process.execArgv.splice(debugArg2Idx, 1);
    }
}

async function removeTemporaryWebpackConfigFiles(farmDirectory: string, logger: ILogger): Promise<void> {
    const files = await readdir(farmDirectory);
    const oldTempFileNames = files.filter(x => x.endsWith(".candy-tmp.webpack.config.js"));
    if (oldTempFileNames.length > 0) {
        logger.log("Removing old webpack.config.js files");
        for (const fileName of oldTempFileNames) {
            await unlink(path.join(farmDirectory, fileName));
        }
        logger.log("Removed old webpack.config.js files");
    }
}

function validateFarmConfig(farmDirectory: string): FarmConfig {
    const farmConfig = readFarmConfig(farmDirectory);

    if (farmConfig.engineDirectory == undefined) {
        throw new Error("EngineDirectory is not specified");
    }
    if (farmConfig.resourcesDirectory == undefined) {
        throw new Error("ResourcesDirectory is not specified");
    }

    const candyModulesDirectory = path.resolve(farmDirectory, "..", ".candy_modules");
    return {
        ...farmConfig,
        farmDirectory: farmDirectory,
        candyModulesDirectory: candyModulesDirectory,
    };
}

const validateAndPrepareFarmDirectoriesPath = async (
    allFarmDirectories: string[],
    currentDir: string,
    context: ConsoleApplicationContext
): Promise<FarmConfig[]> => {
    const result: FarmConfig[] = [];
    for (const pth of allFarmDirectories) {
        if (await pathExists(path.join(pth, "config.js"))) {
            await removeTemporaryWebpackConfigFiles(pth, context.logger);
            result.push(validateFarmConfig(pth));
        } else {
            // Все дело в том, что связка service-runner-2 + tools корректно работает только в папке с формой
            // из за чего, внутри раннера напилен хитрый механизм поиска путей формы. Перепиливать все механики
            // совсем не хочется, поэтому для нас, считаю поднятие до уровня engine вполне приемлемым решением
            const pathToFarm = path.join(currentDir, "..", "..", "..", pth);
            if (!(await pathExists(pathToFarm))) {
                throw Error(`allFarmDirectories path = ${pathToFarm} not exist`);
            }
            await removeTemporaryWebpackConfigFiles(pathToFarm, context.logger);
            result.push(validateFarmConfig(pathToFarm));
        }
    }
    return result;
};

runConsoleApplication(async (argv, context) => {
    remoteInspectFlagToDisableDebuggerInChildProcess();

    context.logger.log("@kontur.candy/tools: " + (await getCurrentVersion()));
    const parsedArgs = parseOptions(argv);

    context.setConsoleLoggingVerbosity(parsedArgs.options.logVerbosity);
    setLogger(context.logger);
    context.logger.verbose(`Verbose level: ${parsedArgs.options.logVerbosity}`);

    if (parsedArgs.type === "init-form") {
        await runInitForm(context, parsedArgs.options);
        return;
    }

    const farmDirectory = process.cwd();
    await removeTemporaryWebpackConfigFiles(farmDirectory, context.logger);
    const farmConfig = readFarmConfig(farmDirectory);

    if (farmConfig.engineDirectory == undefined) {
        throw new Error("EngineDirectory is not specified");
    }
    if (farmConfig.resourcesDirectory == undefined) {
        throw new Error("ResourcesDirectory is not specified");
    }

    const engineDirectory = path.resolve(farmConfig.engineDirectory);
    const resourcesDirectory = path.resolve(farmConfig.resourcesDirectory);
    const candyModulesDirectory = path.resolve(farmDirectory, "..", ".candy_modules");

    if (parsedArgs.type === "build-server" || parsedArgs.type === "dev-build-server") {
        const allFarmDirectories = parsedArgs.options.allFarmsDir;
        context.logger.warning(`allFarmDirectories = ${parsedArgs.options.allFarmsDir}`);
        let allFarmConfig;
        if (Array.isArray(allFarmDirectories) && allFarmDirectories.length > 0) {
            allFarmConfig = await validateAndPrepareFarmDirectoriesPath(allFarmDirectories, farmDirectory, context);
        } else {
            context.logger.warning(`Use default candyFarm path dir = ${farmDirectory}`);
            allFarmConfig = await validateAndPrepareFarmDirectoriesPath([farmDirectory], farmDirectory, context);
        }

        if (parsedArgs.type === "dev-build-server") {
            await runDevBuildServer(context, parsedArgs.options, allFarmConfig);
        } else {
            await runBuildServer(context, parsedArgs.options, allFarmConfig);
        }
    }

    if (parsedArgs.type === "build") {
        await runBuildForms(
            context,
            parsedArgs.options,
            farmDirectory,
            resourcesDirectory,
            engineDirectory,
            candyModulesDirectory
        );
        if (parsedArgs.options.mode === "production") {
            await clearSourceMapForProduction(resourcesDirectory);
        }
    }

    if (parsedArgs.type === "dev-build") {
        await runDevBuild(
            context,
            parsedArgs.options,
            engineDirectory,
            resourcesDirectory,
            farmDirectory,
            candyModulesDirectory
        );
    }

    if (parsedArgs.type === "form-stats") {
        await runFormStats(context, parsedArgs.options, farmDirectory);
    }
    if (parsedArgs.type === "upgrade") {
        await runUpgradeForm(context, parsedArgs.options, farmDirectory);
    }
    if (parsedArgs.type === "rename") {
        await runRenameFiles(context, parsedArgs.options, farmDirectory);
    }
    if (parsedArgs.type === "localize") {
        await runLocalization(context, parsedArgs.options, farmDirectory, engineDirectory, candyModulesDirectory);
    }
});
